import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, IconButton, Icon, Button, useMediaQuery, useTheme } from '@mui/material';
import EditIcon from '../../../icons/editIcon.svg';
import DeleteIcon from '../../../icons/deleteIcon.svg';
import { UpsertApplicantRentalHistoryV2Dto } from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { applicationStyles } from './CustomStyles';
import DeleteModal from './DeleteModal';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import { convertMonthsToYears } from '../../../shared/util/converters';
import { useHistory, useRouteMatch } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  clearCreateRentalHistoryRecord,
  createRentalHistoryRecord,
  deleteRentalHistoryRecord,
  getAllRentalHistoryRecords,
  selectApplicantsState,
} from '../sagas/applicants/applicantsSlice';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { useQuery } from '../../../pmui/transactions/Transactions';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { ApplicationSectionProps } from '../validators/ApplicantValidators';

export default function RentalHistoryDashboard(props: ApplicationSectionProps) {
  const history = useHistory();
  let { url } = useRouteMatch();
  const query = useQuery();
  const hasValidationQueryParam = !!query.get('validation');
  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('sm')); //using material ui media query for consistency
  const classes = applicationStyles();
  const dispatch = useDispatch();

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const {
    isCreatingRentalHistoryRecordSuccess,
    createdRentalHistoryRecord,
    allRentalHistoryRecords,
    isDeletingRentalHistoryRecordSuccess,
    isFetchingAllRentalHistoryRecordsSuccess,
    isFetchingAllRentalHistoryRecordsFailed,
    isCreatingRentalHistoryRecordFailed,
  } = useSelector(selectApplicantsState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsFailed, isFetchingPropertyGroupDetailsSuccess } = useSelector(selectPropertyGroupDetailsState);

  //local states
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedRentalHistoryUuid, setSelectedRentalHistoryUuid] = React.useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState<string | null>(null);
  const doesRentalHistoryHaveCurrentAddress = !!allRentalHistoryRecords.find((rental) => {
    return rental.IsCurrentAddress === true;
  });

  const hasUSBasedAddress = React.useMemo(() => {
    return allRentalHistoryRecords.findIndex((history) => history.Address?.Country?.toLocaleUpperCase() === 'US') !== -1;
  }, [allRentalHistoryRecords]);

  //# region useffects

  //when they click add new rental history, api request succeeds, listen to this
  useEffect(() => {
    if (isCreatingRentalHistoryRecordSuccess && createdRentalHistoryRecord?.Uuid) {
      setSelectedRentalHistoryUuid(createdRentalHistoryRecord?.Uuid);
      dispatch(clearCreateRentalHistoryRecord());
      history.push(`${url}/${createdRentalHistoryRecord?.Uuid}`);
    }
  }, [isCreatingRentalHistoryRecordSuccess, createdRentalHistoryRecord]);

  // on load fetch all rental history records
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid) {
      dispatch({
        type: getAllRentalHistoryRecords.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
        },
      });
    }
  }, [dispatch, ipaUser]);

  //when delete is success, close spinner and re-fetch the rental history
  useEffect(() => {
    if (isDeletingRentalHistoryRecordSuccess) {
      setSpinnerStatus('stop');
      if (ipaUser?.currentProfile.uuid)
        dispatch({
          type: getAllRentalHistoryRecords.type,
          payload: {
            applicantUuid: ipaUser.currentProfile.uuid,
          },
        });
    }
  }, [dispatch, ipaUser, isDeletingRentalHistoryRecordSuccess]);

  //error handling
  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingAllRentalHistoryRecordsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingAllRentalHistoryRecordsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the rental history information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed, isFetchingAllRentalHistoryRecordsFailed, isFetchingAllRentalHistoryRecordsSuccess]);

  //show error if rental history record creation failed
  useEffect(() => {
    if (isCreatingRentalHistoryRecordFailed) {
      setLocalError('There was an error creating a rental history record. Try refreshing the page');
    }
  }, [isCreatingRentalHistoryRecordFailed]);
  //#region end useffects

  //handlers
  const handleConfirmDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setIsSpinnerModalOpen(true);
    dispatch({
      type: deleteRentalHistoryRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        rentalHistoryUuid: selectedRentalHistoryUuid,
      },
    });
  };

  const getDuration = (startDateString?: string, endDateString?: string) => {
    const startDate = startDateString ? new Date(startDateString) : new Date();
    const endDate = endDateString ? new Date(endDateString) : new Date();

    const yearDiff = endDate.getFullYear() - startDate.getFullYear();
    const monthDiff = endDate.getMonth() - startDate.getMonth();

    const totalMonths = yearDiff * 12 + monthDiff;

    return convertMonthsToYears(totalMonths);
  };

  const handleCreateNewRentalHistoryRecord = () => {
    if (doesRentalHistoryHaveCurrentAddress) {
      dispatch({
        type: createRentalHistoryRecord.type,
        payload: { applicantUuid: ipaUser?.currentProfile.uuid, upsertRentalHistoryDto: { RentalHistoryTypeId: 1 } as UpsertApplicantRentalHistoryV2Dto },
      });
    } else {
      dispatch({
        type: createRentalHistoryRecord.type,
        payload: { applicantUuid: ipaUser?.currentProfile.uuid, upsertRentalHistoryDto: { IsCurrentAddress: true, RentalHistoryTypeId: 1 } as UpsertApplicantRentalHistoryV2Dto },
      });
    }
  };

  const handleCloseSpinnerModal = useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, []);

  return (
    <Box width="100%" minHeight="60vh">
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', minHeight: 'inherit', p: 4 }}>
          {localError && (
            <Box sx={{ width: '100%', justifyContent: 'center', textAlign: 'center' }}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Box>
          )}
          <Typography className={classes.labelText}>{`Residential History`}</Typography>
          <Grid container sx={{ mt: 2 }} spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box className={classes.cardContainer}>
                <Box sx={{ px: 1.5 }} className={classes.cardTopContainer}>
                  <Grid container sx={{ width: '100%' }}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'start', alignItems: 'center' }}>
                      <Typography sx={{ my: 2 }} className={classes.bodyText}>
                        {`Please enter ${propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsRentalHistoryYearsRequired || 0} years of residential history.`}{' '}
                        {propertyGroupDetails.ApplicationsSettings?.ApplicationsConfigs?.ScreeningEnabled && !hasUSBasedAddress && allRentalHistoryRecords.length> 0 ? (
                          <Typography component={'span'} sx={{ my: 2 }} className={classes.warningText}>
                            Please include your most recent US based address that you have resided at or received mail to.
                          </Typography>
                        ) : <></>}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={{ height: 260, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '1px',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: 7,
                        },
                        '&::-webkit-scrollbar-track': {
                          boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                          borderRadius: '24px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'darkgrey',
                          borderRadius: '24px',
                        },
                      }}
                    >
                      {allRentalHistoryRecords.map((rentalHistory, idx) => (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            py: 1,
                            borderBottom: '1.5px solid #F1F0F5',
                            justifyContent: 'space-between',
                            flexDirection: isOnMobile ? 'column' : 'row',
                          }}
                          key={rentalHistory.Uuid}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                            <Typography className={classes.cardBodyText}>{rentalHistory.Address?.Line1 || `Address ${idx + 1}`}</Typography>
                            <Typography className={classes.entityHistoryItemSubtitle}>{rentalHistory.IsCurrentAddress ? 'Current Address' : 'Previous Address'}</Typography>
                          </Box>
                          <Box>
                            <Typography className={classes.cardBodyText}>{getDuration(rentalHistory.MoveInDate, rentalHistory.MoveOutDate)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {props.Validator.IsEntireObjectComplete(rentalHistory) ? (
                              <Typography className={classes.entityHistoryCompleteText} mr={2}>
                                Complete
                              </Typography>
                            ) : (
                              <Typography className={classes.entityHistoryInCompleteText} mr={2}>
                                Incomplete
                              </Typography>
                            )}
                            <IconButton
                              onClick={() => {
                                setSelectedRentalHistoryUuid(rentalHistory.Uuid);
                                history.push(`${url}/${rentalHistory.Uuid}${hasValidationQueryParam ? '?validation=true' : ''}`);
                              }}
                              //sx={{ p: 0.25, mr: '-2px' }}
                            >
                              <Icon>
                                <img
                                  style={{
                                    cursor: 'pointer',
                                    width: '14px',
                                    height: '14px',
                                  }}
                                  src={EditIcon}
                                  alt="Edit"
                                />
                              </Icon>
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setSelectedRentalHistoryUuid(rentalHistory.Uuid);
                                setIsDeleteModalOpen(true);
                              }}
                              //sx={{ p: 0.25, mr: '-2px' }}
                            >
                              <Icon>
                                <img
                                  style={{
                                    cursor: 'pointer',
                                    width: '14px',
                                    height: '14px',
                                  }}
                                  src={DeleteIcon}
                                  alt="Delete"
                                />
                              </Icon>
                            </IconButton>
                          </Box>
                        </Box>
                      ))}

                      <Grid container sx={{ px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }}>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'start', alignItems: 'center', textAlign: 'center' }}>
                          <Typography className={classes.cardBodyText}>{doesRentalHistoryHaveCurrentAddress ? 'Add previous address' : 'What is your current address ?'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'end', alignItems: 'center' }}>
                          <Button
                            type="button"
                            className={classes.wizardAddNewBtn}
                            onClick={() => {
                              handleCreateNewRentalHistoryRecord();
                            }}
                          >
                            <Typography className={classes.wizardAddNewBtnText} sx={{ margin: '0 10px' }}>
                              {`Add Address`}
                            </Typography>
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  }
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          fastSpinner={true}
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      {isDeleteModalOpen && <DeleteModal open={isDeleteModalOpen} confirmCallback={handleConfirmDeleteModal} closeCallback={() => setIsDeleteModalOpen(false)} />}
    </Box>
  );
}
